/**
 * Quote Helper Utilities
 * 
 * Shared utility functions for working with Quote objects across wizard steps.
 * Provides consistent handling of required fields and defaults.
 */

import type { Quote } from '../../../types/quote';
import type { Product } from '../../../types/product';
import type { Organization } from '../../../types/organization';
import type { QuoteWizardData } from '../QuoteWizard';

/**
 * Creates a complete Quote object with all required fields populated.
 * Uses existing quote data as base and applies updates, providing sensible defaults
 * for any missing required fields.
 * 
 * @param data - Current wizard data containing quote object
 * @param updates - Partial Quote object with fields to update
 * @returns Complete Quote object with all required fields
 */
export function createCompleteQuote(data: QuoteWizardData, updates: Partial<Quote> = {}): Quote {
  return {
    // Required string fields
    id: data.quote?.id || '',
    quoteNumber: data.quote?.quoteNumber || '',
    templateName: data.quote?.templateName || '',
    status: data.quote?.status || 'DRAFT', // Legacy field - will be removed
    state: data.quote?.state || 'new', // Default to 'new' state
    notes: data.quote?.notes || '',
    productId: data.quote?.productId || '',
    organizationId: data.quote?.organizationId || '',
    createdBy: data.quote?.createdBy || '',
    updatedBy: data.quote?.updatedBy || '',
    
    // Required number fields
    strikeLevel: data.quote?.strikeLevel || 10,
    sumInsured: data.quote?.sumInsured || 0,
    premiumAmount: data.quote?.premiumAmount || 0,
    
    // Required boolean fields
    isMasterPolicy: data.quote?.isMasterPolicy || false,
    
    // Required Date fields
    coverageStartDate: data.quote?.coverageStartDate || new Date(),
    coverageEndDate: data.quote?.coverageEndDate || new Date(),
    validUntil: data.quote?.validUntil || new Date(),
    createdAt: data.quote?.createdAt || new Date(),
    updatedAt: new Date(),
    
    // Required object fields with defaults
    product: data.quote?.product || { 
      id: '<missing>', 
      name: '<missing>', 
      code: '<missing>', 
      version: '1.0.0', 
      status: 'ACTIVE', 
      tags: [], 
      productConfig: {} 
    } as unknown as Product,
    organization: data.quote?.organization || { 
      id: '<missing>', 
      name: '<missing>', 
      code: '<missing>', 
      type: 'INSURANCE_COMPANY' 
    } as unknown as Organization,
    underlying_assets: data.quote?.underlying_assets || { activities_products: [] },
    perils_config: data.quote?.perils_config || { perils: [], total_weight_check: 0 },
    participants: data.quote?.participants || [],
    
    // Optional fields (preserve existing values)
    calculation_results: data.quote?.calculation_results,
    protectionLevels: data.quote?.protectionLevels,
    locationValidation: data.quote?.locationValidation,
    coverageItems: data.quote?.coverageItems,
    quotationJobId: data.quote?.quotationJobId,
    quotationResults: data.quote?.quotationResults,
    selectedScenarios: data.quote?.selectedScenarios,
    approvedAt: data.quote?.approvedAt,
    approvedBy: data.quote?.approvedBy,
    rejectedReason: data.quote?.rejectedReason,
    createdByUser: data.quote?.createdByUser,
    updatedByUser: data.quote?.updatedByUser,
    approvedByUser: data.quote?.approvedByUser,
    
    // Apply updates (overrides defaults)
    ...updates
  };
}
